import { useMemo, useState } from "react";
import { Search, AlertOctagon, Stethoscope, Pill, Briefcase, HardHat, HeartPulse, FlaskConical, BookOpen } from "lucide-react";
import { DISEASES, CATEGORIES, SEVERITY_BG, SEVERITY_LABELS } from "../lib/diseases";
import { MEDICATIONS } from "../lib/medications";
import { DiseaseDetailDialog } from "../components/DiseaseDetailDialog";

const SUGGESTIONS = [
  "shortness of breath",
  "chest pain",
  "tremor",
  "headache",
  "weight loss",
  "tinnitus",
  "skin rash",
  "joint pain",
  "fever",
  "fatigue",
  "memory loss",
  "vomiting",
  "seizures",
  "anemia",
];

// Field → badge color (same hues as the disease detail dialog)
const FIELD_BADGES = {
  symptoms:   { label: "Symptom",     cls: "bg-[#FF8A00] text-zinc-950", icon: HeartPulse },
  cause:      { label: "Cause",       cls: "bg-[#FFD700] text-zinc-950", icon: FlaskConical },
  what:       { label: "Pathology",   cls: "bg-zinc-100 text-zinc-950",  icon: BookOpen },
  diagnosis:  { label: "Diagnosis",   cls: "bg-[#00E5FF] text-zinc-950", icon: Stethoscope },
  treatment:  { label: "Treatment",   cls: "bg-zinc-200 text-zinc-950",  icon: Pill },
  prevention: { label: "Prevention",  cls: "bg-[#FF003C] text-white",    icon: HardHat },
  medication: { label: "Drug",        cls: "bg-[#00E5FF] text-zinc-950", icon: Pill },
  industry:   { label: "Industry",    cls: "bg-zinc-300 text-zinc-950",  icon: Briefcase },
};

// Fields we search and their preview priority
const FIELDS_ORDER = ["symptoms", "cause", "diagnosis", "treatment", "prevention", "what"];

export default function DiseaseSearch() {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return DISEASES
      .map((d) => {
        const matched = [];

        // Field text matching
        for (const f of FIELDS_ORDER) {
          if ((d[f] || "").toLowerCase().includes(q)) matched.push(f);
        }
        // Industries
        if ((d.industries || []).some((i) => i.toLowerCase().includes(q))) matched.push("industry");
        // Medications
        const meds = MEDICATIONS[d.id] || [];
        const medHit = meds.find((m) =>
          (m.name || "").toLowerCase().includes(q) ||
          (m.brand || "").toLowerCase().includes(q) ||
          (m.role  || "").toLowerCase().includes(q)
        );
        if (medHit) matched.push("medication");
        // Name / aka
        const nameHit =
          d.name.toLowerCase().includes(q) ||
          (d.aka || "").toLowerCase().includes(q) ||
          d.code.toLowerCase() === q;

        if (matched.length === 0 && !nameHit) return null;
        return { disease: d, matched, medHit, nameHit };
      })
      .filter(Boolean);
  }, [query]);

  const handleOpen = (disease) => {
    setSelected(disease);
    setOpen(true);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="disease-search-page">
      {/* HERO */}
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" /> Differential · Disease & Drug Search
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            What&apos;s the<br />
            <span className="bg-[#FF8A00] px-2 inline-block">occupational cause?</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-2xl">
            Type a symptom, drug, industry or disease name — we scan all{" "}
            <strong>{DISEASES.length} occupational diseases</strong> across their causes,
            pathology, symptoms, diagnosis, treatment, prevention and pharmacology.
          </p>
        </div>
      </section>

      {/* SEARCH */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-4 py-3 max-w-2xl neo-shadow">
            <Search size={18} strokeWidth={2.5} />
            <input
              data-testid="disease-symptom-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 'shortness of breath', 'tremor', 'doxycycline', 'mining'…"
              className="w-full bg-transparent border-0 outline-none font-body text-base placeholder:text-zinc-500"
              autoFocus
            />
            {query && (
              <button
                data-testid="disease-symptom-clear"
                onClick={() => setQuery("")}
                className="font-mono text-[10px] uppercase tracking-widest underline"
              >Clear</button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Try:</span>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`disease-suggestion-${s}`}
                onClick={() => setQuery(s)}
                className="bg-white border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FF8A00] transition-colors"
              >{s}</button>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {query.trim().length < 2 ? (
          <div className="border-2 border-dashed border-zinc-300 p-10 text-center" data-testid="disease-symptom-empty">
            <Stethoscope size={40} strokeWidth={2} className="mx-auto mb-3 text-zinc-400" />
            <div className="font-mono text-sm text-zinc-600 uppercase tracking-widest">
              Start typing to search 56 diseases
            </div>
          </div>
        ) : results.length === 0 ? (
          <div className="border-2 border-zinc-950 bg-[#FFD700] p-6 text-center" data-testid="disease-symptom-no-results">
            <div className="font-heading font-black text-2xl uppercase">No matches for &ldquo;{query}&rdquo;</div>
            <div className="font-mono text-xs mt-2 uppercase tracking-widest">
              Try a simpler term (e.g. &lsquo;tremor&rsquo; instead of &lsquo;tremors&rsquo;).
            </div>
          </div>
        ) : (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4" data-testid="disease-symptom-count">
              {results.length} disease{results.length === 1 ? "" : "s"} match &ldquo;{query}&rdquo;
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map(({ disease, matched, medHit }) => (
                <ResultCard
                  key={disease.id}
                  disease={disease}
                  matched={matched}
                  medHit={medHit}
                  query={query}
                  onOpen={() => handleOpen(disease)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <DiseaseDetailDialog disease={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

const ResultCard = ({ disease, matched, medHit, query, onOpen }) => {
  const cat = CATEGORIES.find((c) => c.id === disease.cat);
  const sevCls = SEVERITY_BG[disease.severity] || "bg-zinc-300 text-black";

  // Pick the most clinically-relevant matched field for preview
  const previewField =
    matched.includes("medication") ? "medication" :
    matched.includes("symptoms")   ? "symptoms"   :
    matched.includes("treatment")  ? "treatment"  :
    matched.includes("cause")      ? "cause"      :
    matched.includes("diagnosis")  ? "diagnosis"  :
    matched.includes("prevention") ? "prevention" :
    matched.includes("what")       ? "what"       : "symptoms";

  const previewText =
    previewField === "medication" && medHit
      ? `${medHit.name}${medHit.brand && medHit.brand !== "—" ? ` (${medHit.brand})` : ""} — ${medHit.dose}. ${medHit.role}`
      : disease[previewField] || disease.what || "";

  const Icon = (cat?.icon) || Stethoscope;

  return (
    <button
      data-testid={`disease-search-result-${disease.id}`}
      onClick={onOpen}
      className="text-left border-2 border-zinc-950 bg-white neo-shadow hover:neo-shadow-lg hover:-translate-y-1 transition-all duration-200 flex flex-col"
    >
      {/* Header */}
      <div className="grid grid-cols-12 border-b-2 border-zinc-950">
        <div className={`col-span-4 ${cat?.tone || "bg-zinc-200"} p-3 flex flex-col items-center justify-center border-r-2 border-zinc-950`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-80 flex items-center gap-1">
            <Icon size={10} strokeWidth={2.75} /> {cat?.n}
          </div>
          <div className="font-heading font-black text-4xl leading-none tracking-tighter mt-1">
            {disease.code}
          </div>
        </div>
        <div className="col-span-8 p-3 flex flex-col justify-between gap-2">
          <div>
            <div className="font-heading font-bold text-base uppercase tracking-tight leading-tight">
              {disease.name}
            </div>
            <div className={`${sevCls} border-2 border-zinc-950 inline-block px-2 py-0.5 mt-1.5 font-mono text-[9px] uppercase tracking-widest font-bold`}>
              {SEVERITY_LABELS[disease.severity]}
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {matched.slice(0, 4).map((f) => (
              <span
                key={f}
                className={`${FIELD_BADGES[f].cls} border border-zinc-950 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-widest`}
              >
                {FIELD_BADGES[f].label}
              </span>
            ))}
            {matched.length > 4 && (
              <span className="bg-zinc-950 text-white border border-zinc-950 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-widest">
                +{matched.length - 4}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="p-3 flex-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 mb-1">
          {FIELD_BADGES[previewField]?.label || "Preview"}
        </div>
        <p className="text-sm text-zinc-900 leading-snug line-clamp-4">
          <Highlight text={previewText} query={query} />
        </p>
      </div>

      {/* CTA */}
      <div className="border-t-2 border-zinc-950 bg-zinc-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-zinc-950">
          <AlertOctagon size={12} strokeWidth={2.75} />
          {cat?.short}
        </span>
        <span className="underline">Full fact sheet →</span>
      </div>
    </button>
  );
};

const Highlight = ({ text, query }) => {
  if (!query) return text;
  const q = query.trim();
  if (q.length < 2) return text;
  const parts = (text || "").split(
    new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig")
  );
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-[#FFD700] px-0.5">{p}</mark>
      : <span key={i}>{p}</span>
  );
};
